import { memo, useCallback, useMemo, useState } from 'react'

import { Autocomplete, Box, FormControlLabel, ListItemText, Switch, TextField } from '@mui/material'
import { AvcTask, AvcTaskAssociation } from '@nativewaves/nw-cloud-sdk/content'
import { useInfiniteQuery } from '@tanstack/react-query'
import { debounce } from 'lodash-es'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { fetchNextPageOnBottomReached, ListBoxInfinite } from '@shared/components/MaterialUIEnhancements/AutoComplete'
import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { DialogFormContent } from '@shared/layouts'
import { avcTaskQueryKeys } from 'services/queryKeys'

type FormStructure = AvcTaskAssociation

export type LinkDialogFormProps = {
  /** Scopes the search to the stream's own repository. */
  repositoryId?: string
  /** Already-linked ids, disabled in the list so the same task can't be added twice. */
  linkedTaskIds: Set<string>
  /** Stages the association in the surrounding form — nothing is persisted until the form is saved. */
  onLink: (association: AvcTaskAssociation) => void
}

const LinkDialogFormRaw = ({ repositoryId, linkedTaskIds, onLink }: LinkDialogFormProps) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const handleClose = useCloseHandler()

  const [queryValue, setQueryValue] = useState<string>()

  const avcTasksQuery = useInfiniteQuery({
    ...avcTaskQueryKeys.list({ repositoryId, view: 'Slim', query: queryValue, limit: 50 }),
    enabled: !!repositoryId,
  })
  const avcTasks = avcTasksQuery.data?.pages.flatMap((page) => page.items) ?? []

  const debouncedSetQueryValue = useMemo(
    () => debounce((value: string) => setQueryValue(value), 400, { leading: true }),
    [],
  )

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      taskId: '',
      enabled: true,
    },
  })

  const handleFormSubmit = useCallback(
    (association: FormStructure) => {
      onLink(association)
      handleClose()
    },
    [onLink, handleClose],
  )

  return (
    <DialogFormContent
      Illustration={null}
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.AvcTaskAssociations.link')}
      description={t('domain:Workspace.AvcTaskAssociations.linkDescription')}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 3, my: 2 }}>
        <Controller
          name="taskId"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <Autocomplete<AvcTask>
              size="small"
              fullWidth
              openOnFocus
              autoHighlight
              blurOnSelect
              options={avcTasks}
              loading={avcTasksQuery.isLoading}
              value={avcTasks.find((avcTask) => avcTask.id === field.value) ?? null}
              onChange={(_, value) => field.onChange(value?.id ?? '')}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  debouncedSetQueryValue(value)
                }
              }}
              filterOptions={(option) => option}
              getOptionDisabled={(option) => linkedTaskIds.has(option.id)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name || `${t('id')} (${option.id})`}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <ListItemText
                    primary={option.name || `${t('id')} (${option.id})`}
                    secondary={
                      linkedTaskIds.has(option.id) ? t('domain:Workspace.AvcTaskAssociations.alreadyLinked') : option.id
                    }
                  />
                </li>
              )}
              ListboxComponent={ListBoxInfinite}
              ListboxProps={{
                onScroll: fetchNextPageOnBottomReached({
                  hasNextPage: avcTasksQuery.hasNextPage,
                  isFetchingNextPage: avcTasksQuery.isFetchingNextPage,
                  fetchNextPage: avcTasksQuery.fetchNextPage,
                }),
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!fieldState.error}
                  label={t('entity:content.avcTask', { count: 1 })}
                  placeholder={t('domain:Workspace.AvcTaskAssociations.selectTask')}
                />
              )}
            />
          )}
        />

        <Controller
          name="enabled"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch {...field} checked={field.value} />}
              label={t('domain:Workspace.AvcTaskAssociations.enabled')}
            />
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const LinkDialogForm = memo(LinkDialogFormRaw)
